import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * issue #96: backend (Azure Functions proxy) 廃止に伴い、 health check は
 * score-engine の `/api/health` 1 本のみを観測する。 旧 backend pill
 * (`/api/health/backend`) と HealthStatus.backend は撤廃した。
 *
 * - 旧 `useScoreEngineWarmup` (issue #15、 silent ping、 予熱目的) を統合した経緯は
 *   issue #53。 mount + visibilitychange (visible) + 60s throttle の trigger が
 *   silent warmup の役も兼ねる (cold start 緩和)。
 *
 * - timeout 値の根拠:
 *   * SCORE_ENGINE_TIMEOUT_MS = 3000ms: F1 cold start 5 秒目処、 旧 useScoreEngineWarmup
 *     の 3 秒 timeout を踏襲。 mount 直後の UI 確定が遅れないよう 5 秒には伸ばさない。
 *
 * - throttle: 60 秒 (CHECK_THROTTLE_MS)。 mount + visibilitychange (visible) のみ trigger。
 *   定期 polling は cost vs UX 改善が見合わないため採用しない。
 *
 * - issue #115 (cold start 固着): 初回チェックが error に確定した後、 サーバが後から起き上がっても
 *   mount/visibilitychange の trigger が来ない限り「サーバ起動中」banner が固着する。 これを解消するため
 *   error の間だけ HEALTH_RECHECK_INTERVAL_MS 間隔で軽い単発 ping (recovery poll) を撃ち、 ok に
 *   なったら止める。 60s throttle は mount/visibility spam 防止用なので recovery poll は対象外。
 *   `markHealthy()` は「投入成功 = サーバ稼働」の最強シグナルを受けて即 ok に倒し poll を止める。
 *
 * - issue #115 held-open fix (kyon2 本番実測 cold start 34-80s): recovery poll の ping を 3s timeout の
 *   まま撃つと、 cold start window (実測 34-80s) 中は毎回 3s で abort → 永遠に 'ok' に倒れず banner が
 *   固着する。 recovery poll だけ RECOVERY_TIMEOUT_MS = 90000 の held-open 単発 ping にし、 1 本の ping で
 *   cold start を待ち切ってサーバ起床の瞬間に 200 → 'ok' → banner 即消えにする。 mount check (3s) と
 *   markHealthy は据え置き (warm は速いので 3s で十分)。 held-open でも単発性は維持する (前 ping が
 *   resolve するまで次を撃たない) ため多重 ping にはならない。
 *
 * - issue #159 (StrictMode 二重 mount): mount 判定を effect closure の `let mounted` で持つと、
 *   React.StrictMode (index.tsx) の effect 二重実行 (effect → cleanup → effect) で 1 回目の closure の
 *   mounted が false に落ち、 その closure で走行中の pingWithRetry が「unmount された」と誤認して
 *   onFail (serverStarting) も確定反映も丸ごと捨てていた。 2 回目の effect は 60s throttle
 *   (lastCheckAtRef は ref なので二重 mount を跨いで残る) で早期 return するため ping も撃ち直さない。
 *   結果 state が初期値 (pending / serverStarting=false) のまま固着し #141 の banner が出なかった。
 *   mount 判定を hook 生存に紐づく mountedRef にし、 二重実行の再入は inFlightRef で no-op にする。
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const SCORE_ENGINE_PATH = `${API_BASE_URL}/health`;
const SCORE_ENGINE_TIMEOUT_MS = 3000;
const CHECK_THROTTLE_MS = 60 * 1000;
// issue #53 round 2: error の endpoint は 200 が返るまで固定間隔でリトライする (cold start wake-up)。
// 最大 10 回 (初回 + 9 リトライ ≒ 27 秒) で打ち切り、 error を確定する。
const HEALTH_MAX_ATTEMPTS = 10;
const HEALTH_RETRY_INTERVAL_MS = 3000;
// issue #115 (cold start): error 固着からの自動回復 poll 間隔。 recovery ping は held-open (下記
// RECOVERY_TIMEOUT_MS) で長時間かかるので、 この値は ping 間の backoff (instant error 連発時の
// tight-loop 暴走防止) として残す。 小さめでよいので 1 秒。
const HEALTH_RECHECK_INTERVAL_MS = 1000;
// issue #115 held-open fix: recovery poll の ping timeout。 kyon2 本番実測の cold start 34-80s を
// 1 本の held-open ping で待ち切るため、 投入 POST (SUBMIT_TIMEOUT_MS) と同じ思想の 90 秒にする。
// これで cold window 中に abort せず、 サーバ起床の 200 を受信した瞬間に 'ok' に倒れる。
const RECOVERY_TIMEOUT_MS = 90000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export type HealthState = 'pending' | 'ok' | 'error';

export interface HealthStatus {
  scoreEngine: HealthState;
  lastCheckedAt: number | null;
  // issue #141: 「サーバ起動中」banner の表示可否。 health fetch が失敗 (非 200 / reject / 接続エラー) して
  // いる間 true、 ok なら false。 初回 fail attempt で即 true (scoreEngine は pending 維持で pill を即赤に
  // しない = #53)、 初期 pending は false (=#132 フラッシュ回避)。 banner は scoreEngine ではなくこれを見る。
  serverStarting?: boolean;
  // issue #115: 「投入成功 = サーバ稼働」を検出した consumer が health を即健全化するためのフック。
  // scoreEngine を即 'ok' にして recovery poll を止める (安定参照)。
  markHealthy: () => void;
}

async function pingHealth(path: string, timeoutMs: number): Promise<HealthState> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(path, { method: 'GET', signal: controller.signal });
    return res.ok ? 'ok' : 'error';
  } catch {
    return 'error';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * issue #53 round 2: 1 endpoint を ok が返るまで最大 HEALTH_MAX_ATTEMPTS 回リトライする。
 * ok になったら即 return (それ以降のリトライ・点灯遅延を起こさない)。 error は HEALTH_RETRY_INTERVAL_MS
 * 間隔で再試行し、 打ち切りで 'error'。 isMounted() が false (unmount) なら中断して 'pending' を返す
 * (呼び出し側で setStatus を抑止するため)。
 */
async function pingWithRetry(
  path: string,
  timeoutMs: number,
  isMounted: () => boolean,
  // issue #141: ping が失敗するたびに呼ぶ (接続エラー / reject 含む)。 初回 fail で「サーバ起動中」を
  // 即出すため、 scoreEngine 確定 (retry 打ち切り) を待たず serverStarting を立てるフック。
  onFail?: () => void,
): Promise<HealthState> {
  for (let attempt = 0; attempt < HEALTH_MAX_ATTEMPTS; attempt++) {
    if (!isMounted()) return 'pending';
    const state = await pingHealth(path, timeoutMs);
    if (state === 'ok') return 'ok';
    if (isMounted()) onFail?.();
    if (attempt < HEALTH_MAX_ATTEMPTS - 1) await sleep(HEALTH_RETRY_INTERVAL_MS);
  }
  return 'error';
}

// markHealthy を除いた内部 state (setState で扱う実体)。 markHealthy は安定参照のため
// state には持たず、 返却時に合成する (initial state に useCallback を積めない都合も回避)。
type HealthSnapshot = Pick<HealthStatus, 'scoreEngine' | 'lastCheckedAt' | 'serverStarting'>;

// ping 結果を snapshot に確定反映する。 scoreEngine を state に倒し、 serverStarting は
// 「ok 以外なら起動中」の不変条件で同期させる (この対応関係を 1 箇所に集約する)。
function settleHealth(prev: HealthSnapshot, state: HealthState): HealthSnapshot {
  return { ...prev, scoreEngine: state, lastCheckedAt: Date.now(), serverStarting: state !== 'ok' };
}

export function useHealthCheck(): HealthStatus {
  const [status, setStatus] = useState<HealthSnapshot>({
    scoreEngine: 'pending',
    lastCheckedAt: null,
    // issue #141/#132: 初期 pending では起動中扱いにしない (フラッシュ回避)。 fail を観測して初めて true。
    serverStarting: false,
  });
  const lastCheckAtRef = useRef<number>(0);
  // issue #53 round 2 / #159: リトライループ中の unmount で setStatus しないための mounted ガード。
  // closure ではなく ref に持つことで、 StrictMode の effect 二重実行 (cleanup → 再 effect) 後も
  // 「hook はまだ生きている」を正しく表現する (走行中の check の反映が捨てられない)。
  const mountedRef = useRef(false);
  // issue #159: 走行中の check がある間の再入を no-op にする (StrictMode 二重 effect / visibilitychange
  // 連打で ping が多重に走らないようにする)。
  const inFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const isMounted = (): boolean => mountedRef.current;

    const runCheck = async (): Promise<void> => {
      if (inFlightRef.current) return;
      const now = Date.now();
      if (now - lastCheckAtRef.current < CHECK_THROTTLE_MS) return;
      lastCheckAtRef.current = now;
      inFlightRef.current = true;
      try {
        const state = await pingWithRetry(
          SCORE_ENGINE_PATH,
          SCORE_ENGINE_TIMEOUT_MS,
          isMounted,
          // issue #141: 初回 fail (接続エラー含む) で即「サーバ起動中」を出す。 scoreEngine は retry 中
          // pending のまま維持 (pill を即赤にしない = #53)。 呼び出し側 (pingWithRetry) が isMounted()
          // で guard 済みなので、 ここでは mounted 再チェック不要。
          () =>
            setStatus((prev) => (prev.serverStarting ? prev : { ...prev, serverStarting: true })),
        );
        // 'pending' は unmount 中断時のみ返るので反映しない (既存 pending を維持)。
        if (!mountedRef.current || state === 'pending') return;
        setStatus((prev) => settleHealth(prev, state));
      } finally {
        inFlightRef.current = false;
      }
    };
    void runCheck();

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') void runCheck();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // issue #115 (cold start): scoreEngine が error に確定している間だけ、 held-open 単発 ping を
  // HEALTH_RECHECK_INTERVAL_MS backoff で撃つ recovery poll。 mount の runCheck (60s throttle) とは
  // 独立したタイマーで動くため throttle には縛られない。 挙動:
  //   - 'pending' (初回 pingWithRetry 実行中) では張らない → 二重 ping / タイトループを回避。
  //   - ping は RECOVERY_TIMEOUT_MS (90s) の held-open。 前 ping が resolve (ok / 90s abort) する
  //     まで次を撃たない (単発)。 kyon2 実測 cold start 34-80s を 1 本で待ち切り、 起床の 200 で ok。
  //   - error のまま再 ping が返れば lastCheckedAt が更新され本 effect が再実行 → 次の poll を張る。
  //   - ok になれば早期 return + cleanup でタイマーを止める (markHealthy 経由の ok も同様に停止)。
  useEffect(() => {
    if (status.scoreEngine !== 'error') return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        const state = await pingHealth(SCORE_ENGINE_PATH, RECOVERY_TIMEOUT_MS);
        if (cancelled) return;
        setStatus((prev) => settleHealth(prev, state));
      })();
    }, HEALTH_RECHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [status.scoreEngine, status.lastCheckedAt]);

  // issue #115: 投入成功 (jobId 受領) を受けて即 ok に倒す。 scoreEngine が ok になることで
  // recovery poll effect が cleanup され、 以降の ping が止まる。 安定参照 (useCallback)。
  const markHealthy = useCallback(() => {
    setStatus((prev) => (prev.scoreEngine === 'ok' ? prev : settleHealth(prev, 'ok')));
  }, []);

  return { ...status, markHealthy };
}
